import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { analytics } from "../core";
import { usePageTracking } from "./usePageTracking";
import { ensureAnalyticsStarted } from "../bootstrap";
import useAuth from "../../hooks/useAuth";
import type { AppStore } from "../../redux/store";

/**
 * Roles para los que loadOperatorCompanyClient (services/user.service.ts) sí
 * resuelve el company_client_id. Para el resto retorna temprano, así que no hay
 * nada que esperar y el buffer se libera de inmediato.
 */
const ROLES_WITH_ASYNC_COMPANY_CLIENT = ["superadmin", "admin"];

/**
 * Componente hoja (no renderiza nada) que conecta la sesión de Redux con
 * analytics: identify, reset, super properties y vistas de pantalla.
 *
 * Va montado en src/index.js como HERMANO de <App/>, no dentro de App:
 * App.js:28 llama createTheme() sin memo, así que un useLocation() en App
 * reconstruiría el theme MUI completo en cada navegación y re-renderizaría todo
 * componente styled.
 *
 * identify y reset se derivan de las TRANSICIONES del store, no de los puntos de
 * login/logout. Así quedan cubiertos los 4 caminos de login (password, Google,
 * Apple, SSO), el logout de Logout.tsx, el reset por token vencido de App.js y el
 * 401 del interceptor — sin tocar ninguno de esos archivos, varios protegidos.
 */
export const AnalyticsSessionBridge = (): null => {
  // Selectores primitivos a propósito: nunca el objeto `user` completo, que trae
  // email, document_number, address y health_system_* (models/user.model.ts).
  const userId = useSelector((store: AppStore) => store.user?.id ?? "");
  const role = useSelector((store: AppStore) => (store.user?.role as string | undefined) ?? "");
  const companyClientId = useSelector((store: AppStore) => store.user?.company_client_id ?? null);
  const language = useSelector((store: AppStore) => store.user?.language ?? null);

  // Se reusa la regla existente en vez de duplicarla: isEllieCareGeneral mezcla
  // el CC de Ellie Care General con el caso "superadmin sin company_client_id".
  const { isEllieCareGeneral } = useAuth();

  const identifiedFor = useRef<string>("");
  const { pathname } = useLocation();

  // Reintenta el arranque en cada navegación. Es idempotente, y es lo que hace
  // que una sesión iniciada por /sso-bridge —donde el SDK a propósito no se
  // carga— quede medida en cuanto la app sale de esa ruta.
  // Va ANTES de usePageTracking: los efectos corren en el orden en que se
  // declaran los hooks, así que al llegar el primer screen_viewed ya hay adapter.
  useEffect(() => {
    ensureAnalyticsStarted();
  }, [pathname]);

  // ── identify / reset ──────────────────────────────────────────────────────
  useEffect(() => {
    if (userId && identifiedFor.current !== userId) {
      identifiedFor.current = userId;
      analytics.identify(userId, {
        role,
        portal: "backoffice",
        home_company_client_id: companyClientId,
        locale: language,
      });
      return;
    }

    if (!userId && identifiedFor.current) {
      identifiedFor.current = "";
      analytics.track("logged_out", { reason: "user" });
      analytics.reset();
    }
  }, [userId, role, companyClientId, language]);

  // ── Super properties + liberación del buffer ──────────────────────────────
  useEffect(() => {
    // ⚠️ `role` sólo se envía si hay usuario. EmptyUserState (redux/states/user.ts)
    // tiene role: Roles.ADMIN, y es tanto el estado inicial sin sesión como lo que
    // devuelve resetUser(). Sin este guard, un visitante en /login y un usuario
    // recién deslogueado reportarían role: "admin" y contaminarían las métricas.
    analytics.setContext({
      role: userId ? role : null,
      is_ellie_care_general: userId ? isEllieCareGeneral : null,
      locale: language,
    });

    if (!userId) {
      // Rutas públicas: no hay tenant que esperar. Liberar ya para no perder el
      // screen_viewed de /login.
      analytics.markContextReady("anonymous");
      return;
    }

    if (!ROLES_WITH_ASYNC_COMPANY_CLIENT.includes(role)) {
      analytics.markContextReady("resolved");
      return;
    }

    if (companyClientId) analytics.markContextReady("resolved");
    // Si no: queda el timeout del core como red de seguridad.
  }, [userId, role, companyClientId, language, isEllieCareGeneral]);

  usePageTracking();

  return null;
};
